import { unwrap } from '@/lib/apiEnvelope'
import { DOWNLOAD_TIMEOUT_MS, getFileNameFromDisposition, parseBlobError } from '@/lib/download'
import { httpClient } from '@/lib/httpClient'
import { UPLOAD_TIMEOUT_MS } from '@/lib/upload'
import type { ApiResponse, Paginated } from '@/types/api'

import type {
  Employee,
  EmployeeCreated,
  EmployeeCreatePayload,
  EmployeeExportFilters,
  EmployeeListFilters,
  EmployeeListItem,
  EmployeeUpdatePayload,
  ExcluirEmployeePayload,
  TerminateEmployeePayload,
} from '../types/employee'

interface DownloadedFile {
  blob: Blob
  fileName: string
}

async function downloadFile(
  url: string,
  fallbackFileName: string,
  params?: Record<string, string | boolean>,
): Promise<DownloadedFile> {
  const response = await httpClient
    .get<Blob>(url, { params, responseType: 'blob', timeout: DOWNLOAD_TIMEOUT_MS })
    .catch(parseBlobError)
  const disposition = response.headers['content-disposition'] as string | undefined
  return {
    blob: response.data,
    fileName: getFileNameFromDisposition(disposition, fallbackFileName),
  }
}

function buildFilterParams(filters: EmployeeExportFilters): Record<string, string | boolean> {
  const params: Record<string, string | boolean> = {}
  if (filters.nome?.trim()) params.nome = filters.nome.trim()
  if (filters.cargoId) params.cargoId = filters.cargoId
  if (filters.equipeId) params.equipeId = filters.equipeId
  if (filters.regiaoId) params.regiaoId = filters.regiaoId
  if (filters.ativo !== undefined) params.ativo = filters.ativo
  return params
}

function buildParams(filters: EmployeeListFilters): Record<string, string | number | boolean> {
  return { page: filters.page, pageSize: filters.pageSize, ...buildFilterParams(filters) }
}

export const employeeApi = {
  list: async (filters: EmployeeListFilters): Promise<Paginated<EmployeeListItem>> => {
    const response = await httpClient.get<ApiResponse<Paginated<EmployeeListItem>>>('colaborador', {
      params: buildParams(filters),
    })
    return unwrap(response)
  },

  /** Planilha .xlsx com os colaboradores que casam com os filtros (sem paginação). */
  exportXlsx: (filters: EmployeeExportFilters): Promise<DownloadedFile> =>
    downloadFile('colaborador/exportar', 'colaboradores.xlsx', buildFilterParams(filters)),

  downloadFichaRegistro: (id: string): Promise<DownloadedFile> =>
    downloadFile(`colaborador/${id}/ficha-registro`, 'ficha_registro.pdf'),

  getById: async (id: string): Promise<Employee> => {
    const response = await httpClient.get<ApiResponse<Employee>>(`/Colaborador/${id}`)
    return unwrap(response)
  },

  create: async (payload: EmployeeCreatePayload): Promise<EmployeeCreated> => {
    const response = await httpClient.post<ApiResponse<EmployeeCreated>>('/Colaborador', payload, {
      timeout: UPLOAD_TIMEOUT_MS,
    })
    return unwrap(response)
  },

  update: async (id: string, payload: EmployeeUpdatePayload): Promise<Employee> => {
    const response = await httpClient.put<ApiResponse<Employee>>(`/Colaborador/${id}`, payload, {
      timeout: UPLOAD_TIMEOUT_MS,
    })
    return unwrap(response)
  },

  terminate: async (id: string, payload: TerminateEmployeePayload): Promise<void> => {
    await httpClient.patch(`/Colaborador/${id}/desligamento`, payload)
  },

  /** Remove o colaborador do sistema (o GET por id passa a dar 404). 409 se houver pendências. */
  excluir: async (id: string, payload: ExcluirEmployeePayload): Promise<void> => {
    await httpClient.patch(`/Colaborador/${id}/exclusao`, payload)
  },
}
